<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import AmIAffectedByLocalLaw97Image from './images/am_I_affected_by_local_law97_chart.jpg';
	import CarbonReductionVisualizationImage from './images/carbon_reduction_visualization.jpg';
	import InstallingStormWindowInsertsImage from './images/installing_storm_window_inserts.jpg';
	import NycBurrowImage from './images/nyc_burrow.jpg';
	import NycSkylineImage from './images/nyc_skyline.jpg';
	import OldThermostatImage from './images/old_thermostate.jpg';
</script>

<Section>
	<p>
		Cities across the US have introduced carbon emissions legislation to do
		their part to combat climate change. The Big Apple leads the way with the
		biggest climate change legislation, the Climate Mobilization Act. Local Law
		97 is one law within the Climate Mobilization Act that addresses carbon
		emissions of buildings in NYC to make them more sustainable.
	</p>
	<p>
		We will discuss Local Law 97 (LL 97), what it means for building owners and
		managers, affordable housing, and how best to comply with the new
		directives.
	</p>
	<Image
		alt="NYC skyline on a sunny day. NYC Local Law 97 will help to make the largest buildings sustainable."
		src={NycSkylineImage}
		class="w-full"
	/>
</Section>

<Section>
	<h2>What is Local Law 97</h2>
	<p>
		Local Law 97 is a law under the 2019 Climate Mobilization Act that targets
		carbon emissions from the biggest buildings in New York City. Under Local
		Law 97, most buildings 25,000 square feet and larger will be required to
		meet carbon emissions cap, or implement energy efficiency standards, by 2024
		in order to avoid significant fines. The carbon emissions cap will continue
		to tighten periodically. By 2030, most buildings 25,000 sq. ft. and larger
		will have to reduce their emissions by 40%. By 2050, NYC building emissions
		will need to be lowered by 80%. Individual buildings’ emissions from 2005
		will act as the baseline from which emissions will have to be reduced.
	</p>
	<p>
		Buildings are the leading contributor to greenhouse gasses and global
		warming in major cities. By capping carbon emissions from the largest
		buildings in cities across the world, legislators aim to slow the rate of
		global warming and reduce estimates of expected temperature increases in the
		coming decades to avoid worsening natural catastrophes.
	</p>
	<p>
		Beginning in May of 2025, building owners and managers will have to submit
		annual emissions reports to prove that their NYC buildings are meeting the
		new sustainability standards set forth under Local Law 97.
	</p>
</Section>

<Section>
	<h2>What Does LL 97 Mean for New York City Building Owners</h2>
	<p>
		Buildings smaller than 25,000 square feet are exempt from Local Law 97. If
		you are the owner of a building 25,000 square feet or larger, see below to
		understand if you are affected by Local Law 97, and if that means you must
		adhere to carbon emissions caps for your building(s), or if you may opt for
		energy efficiency measures.
	</p>
	<Image
		alt="Flow Chart to determine if your building is affected by LL 97 and NYC carbon emissions cap."
		src={AmIAffectedByLocalLaw97Image}
		class="w-full"
	/>
	<p>
		If you are the owner of a building in New York City and you must adhere to
		carbon emission caps for your building to make it more sustainable, then you
		are subject to fines for failing to do so. Cases of intentionally
		misreported emissions on the annual Greenhouse Gas Emissions report can be
		punishable by fine and imprisonment.
	</p>
	<p>
		To see the amount in fines you would face for your building if you failed to
		meet the carbon emissions cap, use this <a
			href="https://www.be-exchange.org/calculator/"
			target="_blank"
			rel="external">NYC LL97 Carbon Emissions Calculator</a
		>.
	</p>
	<Image
		class="w-full"
		alt="Highrises in NYC. Affordable housing can retrofit windows among other energy efficiency measures."
		src={NycBurrowImage}
	/>
</Section>

<Section>
	<h2>Affordable Housing and Local Law 97</h2>
	<p>
		Under Local Law 97, certain buildings that meet the 25,000 square footage
		threshold to trigger NYC carbon emissions cap requirements have alternatives
		to meeting the 40% carbon emissions reduction. Local Law 97 Guidance for
		Affordable Housing outlines which buildings have options available to them.
		This includes buildings that have at least 35% of its units rent-regulated,
		among others.
	</p>
	<p>
		These buildings have the option to prove by December 31, 2024, that they
		have taken steps to improve their buildings energy efficiency. The <a
			href="https://www1.nyc.gov/site/sustainablebuildings/requirements/affordable-housing.page"
			target="_blank"
			rel="external">Prescriptive Energy Conservation Measures</a
		> include installing individual temperature controls and air sealing windows.
	</p>
	<p>
		The combined benefit of all of the listed energy conservation measures will
		drastically improve the sustainability of large NYC buildings that would
		otherwise be unduly burdened by carbon emissions caps. Insulating windows,
		walls, and ceilings and giving residents of these buildings temperature
		control abilities will go a long way to improving their safety and quality
		of life.
	</p>
	<Image
		src={OldThermostatImage}
		alt="Thermostat. Under LL 97, buildings will be safer & more sustainable with energy efficiency measures."
	/>
</Section>

<Section>
	<h2>Retrofit Windows vs. Replacement Windows</h2>
	<p>
		Windows account for <a
			href="https://www.energy.gov/energysaver/update-or-replace-windows"
			target="_blank"
			rel="external">20% of a building’s energy loss</a
		>. A window upgrade project will go a long way in making your building more
		energy efficient and compliant with increasingly popular carbon emissions
		cap legislation, such as Local Law 97.
	</p>
	<p>
		For window upgrade projects, your options are to retrofit your windows or
		replace them. Replacing windows means removing every part of the window,
		frame and all. This is the most invasive, expensive, time-consuming option,
		and it requires hiring professionals.
	</p>
	<p>
		When talking about retrofit windows, there are two main options: replacing
		the window sash or fitting the frames with window inserts. Replacing the
		sash means the frame stays in place while the moving panels (single, or
		double-hung) are removed. Replacing the sash is an invasive process, but far
		less so than window replacement. The parts are more affordable, and a savvy
		DIYer can complete the project on their own. However, seeking professional
		assistance is recommended since improper replacement of a window sash can
		have the opposite effect than the intended energy efficiency upgrade.
	</p>
	<div class="max-w-full overflow-x-auto">
		<table class="border-collapse">
			<thead class="bg-indow-grey">
				<tr>
					<th></th>
					<th class="border border-b-0 p-4">Window Replacement</th>
					<th class="border border-b-0 p-4" colspan="2">Window Retrofit</th>
				</tr>
				<tr>
					<th class="border border-b-0 p-4">Type</th>
					<th class="border border-b-0 p-4">(Entire Frame Replaced)</th>
					<th class="border border-b-0 p-4">Sash Replacement</th>
					<th class="border border-b-0 p-4">Window Inserts</th>
				</tr>
			</thead>

			<tbody>
				<tr>
					<td
						data-content="Type"
						class="border border-b-0 border-indow-grey-light p-4 text-center"
						>Time</td
					>
					<td
						data-content="(Entire Frame Replaced)"
						class="border border-b-0 border-indow-grey-light p-4 text-center"
						>1-6 Hours</td
					>
					<td
						data-content="Sash Replacement"
						class="border border-b-0 border-indow-grey-light p-4 text-center"
						>~3 Hours</td
					>
					<td
						data-content="Window Inserts"
						class="border border-b-0 border-indow-grey-light p-4 text-center"
						>15-Minute Installation</td
					>
				</tr>
				<tr>
					<td
						data-content="Type"
						class="border border-b-0 border-indow-grey-light p-4 text-center"
						>Cost</td
					>
					<td
						data-content="(Entire Frame Replaced)"
						class="border border-b-0 border-indow-grey-light p-4 text-center"
						>$$$</td
					>
					<td
						data-content="Sash Replacement"
						class="border border-b-0 border-indow-grey-light p-4 text-center"
						>$$</td
					>
					<td
						data-content="Window Inserts"
						class="border border-b-0 border-indow-grey-light p-4 text-center"
						>$</td
					>
				</tr>
				<tr>
					<td
						data-content="Type"
						class="border border-indow-grey-light p-4 text-center"
						>Difficulty</td
					>
					<td
						data-content="(Entire Frame Replaced)"
						class="border border-indow-grey-light p-4 text-center"
						>Professional Required</td
					>
					<td
						data-content="Sash Replacement"
						class="border border-indow-grey-light p-4 text-center"
						>Professional Recommended</td
					>
					<td
						data-content="Window Inserts"
						class="border border-indow-grey-light p-4 text-center">DIY</td
					>
				</tr>
			</tbody>
		</table>
	</div>
	<p>
		Window inserts as a window retrofit project are the most affordable and
		least invasive option for energy efficiency upgrades. And with Local Law 97
		addressing NYC’s largest buildings, the cost, time, and level of
		invasiveness of energy efficiency upgrades are important factors to take
		into account. This least invasive retrofit project, as opposed to frame or
		sash replacement, will allow you to avoid pausing work and life schedules to
		complete.
	</p>
	<Image
		alt="Man installing Indow window inserts in NYC. Retrofitting windows makes buildings more sustainable."
		src={InstallingStormWindowInsertsImage}
		class="w-full"
	/>
</Section>

<Section>
	<h2>Indow & The Clean Fight New York</h2>
	<p>
		<a href="https://www.thecleanfight.com/about" target="_blank" rel="external"
			>The Clean Fight New York</a
		> is a not-for-profit accelerator program in its second year. It was created to
		identify CleanTech companies and assist them in scaling their products for use
		in NYC to make buildings more sustainable, in a way similar to that prescribed
		in Local Law 97. The Clean Fight selects three start-ups each year for each designated
		area of building energy efficiency. This year the categories are Smart Electrification,
		Advanced Optimization, and Envelope Efficiency. Indow was the only start-up included
		in the Envelope Efficiency category addressing energy loss through windows.
	</p>
	<Image
		src={CarbonReductionVisualizationImage}
		alt="4,850 garbage trucks would be needed to haul the 72,0750 tons of carbon Indow inserts have blocked."
		class="w-full"
	/>
	<p>
		With buildings 25,000 square feet and larger requiring drastic measures to
		reduce carbon emissions and energy usage in compliance with Local Law 97,
		full window frame or sash replacement is not financially feasible for
		building owners and managers. The Clean Fight New York is connecting
		building owners and managers with CleanTech companies, and providing the
		blueprint for others as they begin the daunting task of making their huge
		buildings more energy efficient.
	</p>
	<p>
		Read about the <a href="/resources/case-studies/energy-efficiency-old-homes"
			>Department of Energy’s study of Indow inserts</a
		> to learn more about why our inserts were chosen for The Clean Fight New York
		and how they can help your NYC building become sustainable and meet energy conservation
		measures under Local Law 97.
	</p>
</Section>

<Reviews />
